/**
 * @file ThemeProvider.js
 * @module src/js/contexts/ThemeProvider
 * @description
 * **Provides theme-related context to the EDALab frontend.**
 * - Initializes theme from `localStorage`, or chooses based on the current hour (light during day, dark at night).
 * - Persists user-selected theme in `localStorage`.
 * - Exposes `theme`, `setTheme`, and `themeTransitionDuration` via React Context to all child components.
 * - Please use `useTheme()` to access or update the theme within any component.
 *
 * @version
 * <p>
 *  2025-07-22: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Added themeTransitionDuration for consistent theme change animations. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { createContext, useState, useEffect, useContext } from 'react';

const ThemeContext = createContext();

export default function ThemeProvider({ children }) {
    const themeTransitionDuration = 0.2; // seconds

    // Load from localStorage (or default value, based on the current hour)
    const currentHour = new Date().getHours();
    const [theme, setTheme] = useState(localStorage.getItem("theme") ||
        (currentHour >= 6 && currentHour < 18 ? "light" : "dark"));

    // Save state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem("theme", theme);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, themeTransitionDuration }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);